import { QueryClient } from "@tanstack/react-query";
import {
  useState,
  useEffect
} from "react";
import {
  createBrowserRouter,
  redirect,
  RouterProvider as ReactRouterRouterProvider,
  useRouteError
} from "react-router-dom";
import fetchUserMgmt from "./fetchUserMgmt";
import { useNavigate } from "./navigate";
import {
  Agreements,
  Login,
  OnBoarding,
  Scorm,
  Password,
  PasswordChange,
  PasswordCode,
  PasswordRecovery,
  ProfileCapabilities,
  ProfileProfession,
  ProfileJob,
  ProfilePreferences,
  ProfileSkills,
  SignupDomain,
  SignupSummary,
  Modal
} from "../components";
import Register, { loader as registerLoader } from "../components/Register/Register.component";
import { AUTH_MODE, NAVBAR_ITEMS } from "../consts";
import { RegisterStateProvider } from "../context/RegisterContext";
import { UserNavigationContext, useUserNavigationProvider } from "../context/UserNavigationContext";
import {
  Access,
  ActivitiesDetailPage,
  ActivitiesRelatedPage,
  Agenda,
  Bookmarks,
  Category,
  Collection,
  Community,
  CommunityDetails,
  CookiePage,
  Explore,
  ExploreDetail,
  ExploreWatch,
  Homepage,
  Landing,
  Lesson,
  NotFoundPage,
  PrivacyPage,
  Profile,
  Refresh,
  Root,
  Search,
  SmartLearning,
  Test,
  TermsAndCondition,
  Maintenance
} from "../routes";
//import { AdaptiveQuizPage } from "../routes/AdaptiveQuiz";
import { Academy } from "../routes/Academy";
import { IconSetPage } from "../routes/IconSet";
import { SSORedirectErrorPage } from "../routes/SSORedirectError";
import { useAuthStore, useNavbarStore } from "../stores";
import { getError } from "../utils/error";
import { getEnvironmentVariables } from "../utils/general";
import { getLoginType, LoginMode } from "../utils/login";
import { CustomRegex } from "../utils/regex";

const { basePath, clientId } = getEnvironmentVariables();

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchInterval: Infinity,
      refetchOnMount: "always",
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        let shouldGiveUp = false;
        if (typeof error === "string") { //|| error instanceof(String)) {
          try {
            const parsedError = JSON.parse(error ?? {});
            if (parsedError.errors instanceof Object) {
              shouldGiveUp = Object.values(parsedError.errors).some((errorCategory) => {
                if (!Array.isArray(errorCategory)) return false;
                return errorCategory.some((error) => error.retry === false);
              });
            }
          } catch {
            shouldGiveUp = false;
          }
        }
        if (failureCount < 3 && !shouldGiveUp) {
          return true;
        } else {
          // eslint-disable-next-line no-console
          // console.error("last retry attempt failed\n", error);
          return false;
        }
      },
      retryDelay: (attemptIndex) => (
        Math.min(1000 * 5 ** attemptIndex, 30000)
      )
      // the staleTime setting is only valid on the hook itself, defaults to 0
    }
  }
});

export const RouterProvider = () => {
  const isAuthenticated = useAuthStore(state => Boolean(state.session));
  const signInSSO = useAuthStore(state => state.signInSSO);
  const signOut = useAuthStore(state => state.signOut);
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);
  const userRoles = useAuthStore(state => state.userData?.roles);
  const isPO = userRoles?.includes("PRODUCT_OWNER");
  const maintenanceGlobal = useAuthStore(state => state.maintenanceGlobal);
  const hasInitiative = useAuthStore(state => Boolean(state.userData?.initiative_id));
  const { navigation, setNavigation } = useUserNavigationProvider();
  const authSSOIsAllowed = useAuthStore(state =>
    state.allowedAuthentications.includes(AUTH_MODE.SSO)
  );
  const setSpinnerLoginVisible = useAuthStore(state => state.setSpinnerLoginVisible);
  const ssoEndpoint = useAuthStore(state => state.ssoEndpoint);
  const disableSSORedirect = sessionStorage.getItem("disable_sso_redirect");
  const registrationCompletedVisible = useAuthStore(state => state.registrationCompletedVisible);
  //const setIsLoading = useAuthStore(state => state.setIsLoading);

  const exploreElementsRoutes = [
    {
      element: <ExploreDetail />,
      paths: [
        "/esplora/dettaglio/:id/:learningObjectTypology",
        "/esplora/dettaglio/:id/root/:rootId/:learningObjectTypology",
        "/esplora/dettaglio/:id/course/:courseId/:learningObjectTypology",
        "/esplora/dettaglio/:id/course/:courseId/root/:rootId/:learningObjectTypology",
        "/per-te/dettaglio/:id/:learningObjectTypology",
        "/per-te/dettaglio/:id/root/:rootId/:learningObjectTypology",
        "/per-te/dettaglio/:id/course/:courseId/:learningObjectTypology",
        "/per-te/dettaglio/:id/course/:courseId/root/:rootId/:learningObjectTypology"
      ]
    },
    {
      element: <ExploreWatch />,
      loader: ({ params }: { params: Record<string, string | undefined> }) => {
        const { id, learningObjectTypology } = params;
        // Valida che i parametri essenziali siano presenti e validi
        if (!id || isNaN(Number(id)) || !learningObjectTypology) {
          return redirect("/not-found");
        }
        return null;
      },
      paths: [
        "/esplora/guarda/:id/:learningObjectTypology",
        "/esplora/guarda/:id/root/:rootId/:learningObjectTypology",
        "/esplora/guarda/:id/course/:courseId/:learningObjectTypology",
        "/esplora/guarda/:id/course/:courseId/root/:rootId/:learningObjectTypology",
        "/per-te/guarda/:id/:learningObjectTypology",
        "/per-te/guarda/:id/root/:rootId/:learningObjectTypology",
        "/per-te/guarda/:id/course/:courseId/:learningObjectTypology",
        "/per-te/guarda/:id/course/:courseId/root/:rootId/:learningObjectTypology"
      ]
    }
  ];

  const exploreRoutes = exploreElementsRoutes.flatMap(({ element, paths, loader }) =>
    paths.map(path => ({ element, path, ...(loader && { loader }) }))
  );

  const router = createBrowserRouter([
    {
      children: [
        {
          children: [
            // login form
            {
              element:<Login />,
              loader: async ({ request }) => {
                const url = new URL(request.url);
                const fromUrl = await url.searchParams.get("fromUrl");
                const { loginMode } = await getEnvironmentVariables();
                const loginModeTyped = loginMode as LoginMode;
                const autoRedirectSsoLogin = ()=>{
                  // Se c'è fromUrl, lo codifica e lo aggiunge al parametro state
                  if(ssoEndpoint){
                    // Gestione compatibilità: se ssoEndpoint è una stringa, lo convertiamo in URL
                    const urlEndpoint = typeof ssoEndpoint === "string" ? new URL(ssoEndpoint) : ssoEndpoint;
                    if (fromUrl) {
                      urlEndpoint.searchParams.set("state", `from_url=${encodeURIComponent(fromUrl)}`);
                    }
                    const ssoUrl = urlEndpoint.href.replace("%2B", "+");
                    window.location.replace(ssoUrl);
                  }
                };


                const userMgmtData = await fetchUserMgmt({ basePath, clientId });

                if(getLoginType(loginModeTyped) !== "sso"){
                  setSpinnerLoginVisible(false);
                  return userMgmtData;
                }

                if (isAuthenticated) {
                  //setSpinnerLoginVisible(false); FORSE DA SETTARE
                  return redirect("/");
                  //return userMgmtData;
                }

                if(disableSSORedirect){//
                  return redirect("/sso-redirect-error");
                // basari sull getLoginType(loginModeTyped) === "sso"
                }

                if(!userMgmtData?.splash && getLoginType(loginModeTyped) === "sso" && authSSOIsAllowed && ssoEndpoint && registrationCompletedVisible === null){
                  autoRedirectSsoLogin();
                  return userMgmtData;
                  //come gestire l'errore?

                }

                if(getLoginType(loginModeTyped) === "sso"|| userMgmtData?.splash && authSSOIsAllowed ){
                  setSpinnerLoginVisible(false);
                  return userMgmtData;
                }

                if(registrationCompletedVisible !== null){
                  setSpinnerLoginVisible(false);
                  return userMgmtData;
                }

                return userMgmtData;
              },
              path: "login"
            },
            // password
            {
              element: <Password />,
              path: "password",
              loader: async () => {
                return await fetchUserMgmt({ basePath, clientId });
              }
            },
            {
              element: <PasswordChange />,
              path: "password/change"
            },
            {
              element: <PasswordRecovery />,
              loader: async () => {
                signOut(null);
                return await fetchUserMgmt({ basePath, clientId });
              },
              path: "password/recovery"
            },
            {
              element: <PasswordCode />,
              loader: async () => {
                return await fetchUserMgmt({ basePath, clientId });
              },
              path: "password/code"
            },
            // select initiative domain
            {
              element: <SignupDomain />,
              path: "portal"
            }
          ],
          element: <Access/>,
          loader: async ({ request }) => {
            const path = CustomRegex.matchPathLeaf(request.url);
            if (path === "access") {
              return redirect("/access/login");
            } else {
              return null;
            }
          },
          path: "access"
        },
        {
          // profile
          children: [
            {
              element: <ProfileProfession/>,
              path: "profession"
            },
            {
              element: <ProfileJob />,
              path: "job"
            },
            {
              element: <ProfileSkills />,
              path: "skills"
            },
            {
              element: <ProfileCapabilities />,
              path: "capabilities"
            },
            {
              element: <ProfilePreferences />,
              path: "preferences"
            },
            {
              element: <SignupSummary />,
              path: "summary"
            }
          ],
          element: <OnBoarding/>,
          path: "signup"
        },
        // consents
        {
          element: <Agreements/>,
          path: "agreements"
        },
        {
          element: <RegisterStateProvider><Register/></RegisterStateProvider>,
          loader: ()=> registerLoader({ basePath, clientId }),
          path: "register"
        },
        // pages
        {
          element: <Agenda/>,
          path: "agenda"
        },
        {
          element: <Bookmarks/>,
          path: "bookmarks"
        },
        {
          element: <Collection/>,
          path: "percorso/:id"
        },
        ...exploreRoutes,
        // {
        //   element: <Example/>,
        //   loader: loader([
        //     contentsQuery,
        //     eventsQuery,
        //     packagesQuery
        //   ]),
        //   path: "example"
        // },
        /*,
        Funzionalità già rilasciata ma nascosta
        {
          element: <AdaptiveQuizPage/>,
          path: "/esplora/quiz-adattivo"
        }*/
        {
          element: <Category/>,
          path: "/esplora/categories/:category"
        },
        {
          element: <Explore/>,
          path: "esplora"
        },
        {
          element: <Homepage/>,
          loader: async() => {
            if(smartConfiguration?.visForyou === false){
              return redirect("/");
            }
            else {
              return null;
            }
          },
          // loader(
          //   [
          //     // getStructuresQuery(
          //     //   refreshSession,
          //     //   {
          //     //     corporateId,
          //     //     idToken,
          //     //     initiativeId,
          //     //     pageName: PAGE_NAME.FOR_YOU_OVERVIEW,
          //     //     sessionToken,
          //     //     smartConfiguration
          //     //   }
          //     // )
          //   ])
          path: "per-te"
        },
        {
          element: <ActivitiesDetailPage/>,
          path: "activitiesDetail"
        },
        {
          element: <ActivitiesRelatedPage/>,
          path:"/esplora/related/:id"
        },
        {
          element: <Landing/>,
          path: "welcome"
        },
        {
          element: <Community/>,
          path: "community"
        },
        {
          element: <CommunityDetails/>,
          path: "community/:id"
        },
        {
          element: <CookiePage/>,
          path: "cookie"
        },
        {
          element: <Lesson/>,
          path: "lezione/:id"
        },
        {
          element: <TermsAndCondition/>,
          path: "terms-conditions"
        },
        {
          element: <PrivacyPage/>,
          path: "privacy"
        },
        {
          children: [
            {
              element: <Profile />,
              path: ""
            },
            {
              element: <ProfilePreferences />,
              path: "preferences"
            }
          ],
          path: "profile"
        },
        {
          element: <Search/>,
          path: "search/:term"
        },
        {
          element: <SmartLearning/>,
          path: "smart-learning"
        },
        {
          element: <NotFoundPage/>,
          path: "not-found"
        },
        {
          element: <IconSetPage/>,
          path: "icons"
        },
        {
          element: <Academy/>,
          path: "academy/:id"
        },
        // NOTE: test is meant for components which have issues with storybook
        // TODO: try out with storybook once updated to v7, starting 23/04/21
        {
          element: <Test/>,
          path: "colors"
        }
      ],
      element: <Root/>,
      errorElement: <RouterErrorBoundary />,
      loader: async({ request }) => {
        if ((!isPO && maintenanceGlobal && isAuthenticated) || (!isPO && hasInitiative && smartConfiguration?.showMaintenancePage)) {
          return redirect("/maintenance-page");
        }

        document.getElementById("lds-ring")?.remove();
        const requestUrl = new URL(request.url);
        const ssoCode = requestUrl.searchParams.get("code");
        const seedSession = requestUrl.searchParams.get("seed_session");
        if(seedSession){
          localStorage.clear();
        }
        //Se viene lasciato questo if non viene aggiornato lo userMgmtData allo switch ADMIN-LEAENER 
        //o nel caso in cui abbiamo nella localStorage dati obsoleti viene utilzzato lo userMgmt con dati appunto obsoleti.
        // bug riscontrato se si va a cambiare nella corporate di appartenenza la voce "tutorial"
        //let userMgmtData = useAuthStore.getState().userMgmtData;
        //if(!userMgmtData){
        let userMgmtData = await fetchUserMgmt({ basePath, clientId });
        useAuthStore.getState().setUserMgmtData(userMgmtData);
        // }

        if(ssoCode && !isAuthenticated && registrationCompletedVisible === null) {
          const uuidQRCode = localStorage.getItem("uuid_qrcode");
          if(uuidQRCode) window.history.replaceState({}, document.title, window.location.pathname);
          signInSSO(ssoCode);
        }
        // fix apertura per-te => incolla url esplora/dettaglio... rimaneva il per-te attivo
        requestUrl.pathname.startsWith("/per-te") && useNavbarStore.getState().setNavbarItem(NAVBAR_ITEMS.FOR_YOU);
        requestUrl.pathname.startsWith("/esplora") && useNavbarStore.getState().setNavbarItem(NAVBAR_ITEMS.EXPLORE);

        return userMgmtData;

        // return null;
        // const subRoute = CustomRegex.matchSubRoute(request.url);
        // const region = subRoute ? CustomRegex.matchDirectChild(subRoute, "") : "";
        // if (!isAuthenticated) {
        //   return redirect("/access/login");
        // } else if (region === "") {
        //   return redirect("/per-te");
        // } else {
        //   return null;
        // }
      },
      path: ""
    },
    {
      element: <Scorm/>,
      loader: async() => {
        document.getElementById("lds-ring")?.remove();
        return null;
      },
      path: "/esplora/scorm/:id/:learningObjectTypology"
    },
    {
      //TODO: add ui with logout notice
      element: <></>,
      loader: async({ request }) => {
        document.getElementById("lds-ring")?.remove();

        // Estrae fromUrl dalla query string se presente
        const requestUrl = new URL(request.url);
        const fromUrl = requestUrl.searchParams.get("fromUrl");
        const shouldSaveFromUrl = Boolean(fromUrl);

        // Esegui il logout (questo pulisce la sessione)
        signOut("/", true, shouldSaveFromUrl);

        // Redirect diretto al login con fromUrl come state
        if (fromUrl) {
          // Non possiamo passare state tramite redirect(), quindi aggiungiamo fromUrl come query param
          return redirect(`/access/login?fromUrl=${encodeURIComponent(fromUrl)}`);
        } else {
          return redirect("/access/login");
        }
      },
      path: "/logout"
    },
    {
      element: <Refresh />,
      loader: async() => {
        document.getElementById("lds-ring")?.remove();
        return null;
      },
      path: "/refresh"
    },
    {
      element: <Maintenance />,
      path: "maintenance-page"
    },
    {
      element: <SSORedirectErrorPage/>,
      path: "sso-redirect-error"
    },
    {
      loader: async () => {
        /*if (!isAuthenticated) {
          return redirect("/access/login");
        }else  */
        if (isAuthenticated) {
          return redirect("/not-found");
        }
        return null;
      },
      path: "*"
    }
  ]);

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log("💻 ",import.meta.env.VITE_APP_NAME, "Env-Mode: ", import.meta.env.VITE_APP_NODE_ENV);
  }, []);

  return (
    <UserNavigationContext.Provider value={ { navigation, setNavigation } }>
      <ReactRouterRouterProvider router={ router }/>
    </UserNavigationContext.Provider>

  );
};

function RouterErrorBoundary() {
  // const routeError = useRouteError() as {
  //   data: string
  //   error: Error
  //   internal: boolean
  //   status: number
  //   statusText: string
  // };
  const routeError = useRouteError();

  const [show, setShow] = useState(true);
  const navigate = useNavigate();

  const err = getError(routeError)?.[0];

  // const error = getError(routeError.error);
  /* eslint-disable no-console */
  routeError && console.warn("RouterErrorBoundary", { routeError, show });
  /* eslint-enable no-console */
  return (
    <Modal
      description={ err?.message ?? "unknown" }
      open={ show }
      onClose={ () => {
        setShow(false);
        navigate("/");
      } }
      title={ err?.name ?? "Error" }
    />
  );
}
